import React, { useEffect, useRef, useState } from "react";
import "./ReelCard.css";

export const ReelCard = ({
  userId,
  property,
  isMuted,
  setIsMuted,
  onAddFavorite,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isFavorite, setIsFavorite] = useState(
    property?.IsReelFavorite || false
  );

  const shareMenuRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play();
          setIsPlaying(true);
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      {
        threshold: 0.6,
      }
    );

    observer.observe(video);

    return () => {
      observer.unobserve(video);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = (e) => {
    const video = videoRef.current;
    if (video && video.duration) {
      const current = e.target.currentTime;
      const total = e.target.duration;
      setProgress((current / total) * 100);
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const seekTime = (clickX / rect.width) * video.duration;
    video.currentTime = seekTime;
  };

  const changeSpeed = (rate) => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  const handleFavoriteClick = async () => {
    if (onAddFavorite) {
      const success = await onAddFavorite(property.ItemId);
      if (success) {
        setIsFavorite(!isFavorite);
      }
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        shareMenuRef.current &&
        !shareMenuRef.current.contains(event.target)
      ) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!property.ReelUrl) return null;

  return (
    <div className="d-flex flex-column w-100 flex-md-row h-100">
      <div className="flex-grow-1">
        <div className="reels-instagram-wrapper">
          <div className="reel-ig-card">
            <video
              ref={videoRef}
              className="reel-ig-video"
              src={property?.ReelUrl}
              autoPlay
              loop
              playsInline
              poster="/images/reel-thumbnail.jpg"
              onClick={(e) => togglePlayPause(e.target)}
              onTimeUpdate={handleTimeUpdate}
            ></video>

            {!isPlaying && (
              <div className="reel-ig-center-icon">
                <i className="fas fa-play"></i>
              </div>
            )}

            <button className="reel-ig-mute-btn" onClick={toggleMute}>
              <i
                className={`fas ${isMuted ? "fa-volume-mute" : "fa-volume-up"}`}
              ></i>
            </button>

            <div className="reel-ig-overlay">
              <div className="reel-ig-title">{property.ItemName}</div>
            </div>

            <div className="reel-ig-bottom-controls-wrapper">
              <div className="reel-ig-progress-bar" onClick={handleSeek}>
                <div
                  className="reel-ig-progress"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>

              <div className="reel-ig-speed-dropdown">
                <select
                  value={playbackRate}
                  onChange={(e) => changeSpeed(Number(e.target.value))}
                >
                  <option value={0.5}>0.5x</option>
                  <option value={1.0}>1x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
                <i className="fas fa-caret-down dropdown-icon"></i>
              </div>
            </div>
            <div className="reel-ig-actions">
              <a
                href={`https://wa.me/9824712471?text=${encodeURIComponent(
                  `Hello Karma, I'm interested in ${property.ItemName}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="reel-ig-action-btn"
                title="Chat on WhatsApp"
              >
                <i className="fab fa-whatsapp text-success"></i>
              </a>

              <button
                className="reel-ig-action-btn"
                title={isFavorite ? "Non-favorite" : "Favorite"}
                onClick={() => {
                  if (!userId || userId === 0) {
                    // window.location.href = "/Authenticate/GetUserMobile";
                    window.location.href = `/Authenticate/Login?returnUrl=${encodeURIComponent(
                      "/Home/Reel"
                    )}`;
                    return;
                  } else {
                    handleFavoriteClick();
                  }
                }}
              >
                <i
                  className={
                    isFavorite ? "fas fa-heart text-danger" : "far fa-heart"
                  }
                ></i>
              </button>

              <div className="reel-ig-share-wrapper" ref={shareMenuRef}>
                <button
                  className="reel-ig-action-btn"
                  title="Share"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                >
                  <i className="fas fa-share"></i>
                </button>

                <div className={`share-menu ${showShareMenu ? "show" : ""}`}>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(
                      (() => {
                        const url = new URL(window.location.href);

                        url.searchParams.set("itemId", property?.ItemId);

                        url.searchParams.delete("myreels");

                        // return `Check out this Property Reel! \n${url.toString()}`;
                        return `Check out this Property Reel: ${
                          property?.ItemName
                        }\n${url.toString()}`;
                      })()
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon text-decoration-none"
                    title="Share on WhatsApp"
                  >
                    <i className="fab fa-whatsapp"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
